// --------------------------------------------------------------
// Created On: 2023-02-17
// Author: Zachary Thomas
//
// Last Modified: 2025-11-02
// Modified By: Zachary Thomas
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState, useEffect } from "react";
import Spinner from "../../components/Spinner/Spinner";
import useApi from "../../hooks/useApi";
import Error from "../../components/Error/Error";
import Card from "../../components/Card/Card";
import AdvancedSearch from "./AdvancedSearch/AdvancedSearch";
import SearchListContainer from "./SearchListContainer/SearchListContainer";
import apiRequest from "../../utilities/api/apiRequest";
import getApiError from "../../utilities/api/getApiError";
import { RPM_COMPANY } from "../../constants/miscellaneous";
import { useParams } from "react-router-dom";
import useReduxSelector from "../../hooks/useReduxSelector";
import { API } from "../../constants/miscellaneous";
import styles from "./SearchPage.module.scss";
import deepCopy from "src/utilities/deepCopy";

// Page for searching assets and monitoring devices.
export default function SearchPage(): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [isAdvancedSearch, setIsAdvancedSearch] = useState<boolean>(false);
  const [basicSearchQuery, setBasicSearchQuery] = useState<string>("");
  const [assetName, setAssetName] = useState<string>("");
  const [assetNickname, setAssetNickname] = useState<string>("");
  const [productType, setProductType] = useState<string>("");
  const [productManufacturer, setProductManufacturer] = useState<string>("");
  const [productModel, setProductModel] = useState<string>("");
  const [productIdentifier, setProductIdentifier] = useState<string>("");
  const [controllerModel, setControllerModel] = useState<string>("");
  const [deviceModel, setDeviceModel] = useState<string>("");
  const [deviceIdentifier, setDeviceIdentifier] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [assets, setAssets] = useState<Asset[]>([]);
  const [deviceStatuses, setDeviceStatuses] = useState<DeviceStatusResponseBody[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [companyId, setCompanyId] = useState<number>(0);
  const [productTypes, setProductTypes] = useState<string[]>([]);
  const [controllers, setControllers] = useState<ControllerSelection[]>([]);
  const [deviceTypes, setDeviceTypes] = useState<DeviceTypeSelection[]>([]);
  const [selectedTabId, setSelectedTabId] = useState<string>("assets");
  const { initialSearchQuery } = useParams<Params>();
  const currentUser = useReduxSelector((state) => state.currentUser);
  const tabs: Tab[] = [
    {
      tabId: "assets",
      name: `Assets (${assets.length})`,
      shortName: `Assets (${assets.length})`,
      breakPoints: "col",
    },
    {
      tabId: "monitoring-devices",
      name: `Monitoring Devices (${devices.length})`,
      shortName: `Devices (${devices.length})`,
      breakPoints: "col",
    },
  ];

  // If the user got to this page by using a basic search bar, start with a basic search.
  useApi(
    () => {
      if (initialSearchQuery !== undefined) {
        setBasicSearchQuery(initialSearchQuery);
        setIsAdvancedSearch(false);
        setLoading(true);
        return true;
      } else {
        return false;
      }
    },
    {
      method: "POST",
      url: `${API}/company/${currentUser.companyName === RPM_COMPANY ? 0 : currentUser.companyId}/asset/search`,
      body: {
        assetSearchQuery: initialSearchQuery,
        assetId: null,
        assetName: null,
        assetNickname: null,
        productType: null,
        productManufacturer: null,
        productModel: null,
        productIdentifier: null,
        controllerModel: null,
        controllerManufacturer: null,
        deviceModel: null,
        deviceIdentifier: null,
      },
    },
    async (response: Response, responseBody: PostResponseBody) => {
      if (response.ok && responseBody) {
        if (initialSearchQuery !== undefined) {
          setBasicSearchQuery(initialSearchQuery);
        }
        setAssets(responseBody.assets);
        setDevices(responseBody.devices);
        setDeviceStatuses(responseBody.deviceStatuses);
        setErrorMessage("");
      } else {
        setErrorMessage(await getApiError(response, "Unable to search assets."));
      }
      setLoading(false);
    },
    [initialSearchQuery]
  );

  // Get data for select and data list options.
  useApi(
    () => {
      return true;
    },
    {
      method: "GET",
      url: `${API}/company/${currentUser.companyId}/asset/search/map`,
    },
    async (response: Response, responseBody: GetResponseBody) => {
      if (response.ok && responseBody) {
        setProductTypes(responseBody.productTypes);
        setControllers(responseBody.controllers);
        setDeviceTypes(responseBody.deviceTypes);
        setErrorMessage("");
      } else {
        setErrorMessage("Internal server error. Unable to get form data.");
      }
      setLoading(false);
    },
    []
  );

  // Get the user's company ID. In the case of the RPM company, set company ID to 0 to search all companies.
  useEffect(() => {
    if (currentUser.companyName === RPM_COMPANY) {
      setCompanyId(0);
    } else {
      setCompanyId(currentUser.companyId);
    }
  }, [JSON.stringify(currentUser)]);

  // Format devices to include status names, values, and descriptions.
  function formatDevices(devices: Device[], deviceStatusLookUps: DeviceStatusResponseBody[]): Device[] {
    const devicesDeepCopy = deepCopy(devices);
    devicesDeepCopy.forEach((device) => {
      device.statuses.forEach((deviceStatus, i) => {
        const statusLookUp = deviceStatusLookUps.find(
          (statusLookUp) => statusLookUp.deviceStatusId === deviceStatus.deviceStatusId
        );
        if (statusLookUp !== undefined) {
          const status = {
            deviceStatusId: deviceStatus.deviceStatusId,
            value: deviceStatus.value,
            name: statusLookUp.name,
            code: statusLookUp.code,
            description: statusLookUp.description,
          };
          device.statuses[i] = status;
        }
      });
    });
    return devicesDeepCopy;
  }

  // Search for assets.
  async function assetSearch(): Promise<void> {
    if (!loading) {
      const requestBody = {
        assetSearchQuery: basicSearchQuery.length > 0 ? basicSearchQuery : null,
        assetId: null,
        assetName: assetName.length ? assetName : null,
        assetNickname: assetNickname.length ? assetNickname : null,
        productType: productType.length ? productType : null,
        productManufacturer: productManufacturer.length ? productManufacturer : null,
        productModel: productModel.length ? productModel : null,
        productIdentifier: productIdentifier.length ? productIdentifier : null,
        controllerModel: controllerModel.length ? controllerModel : null,
        controllerManufacturer: null,
        deviceModel: deviceModel.length ? deviceModel : null,
        deviceIdentifier: deviceIdentifier.length ? deviceIdentifier : null,
      };

      setLoading(true);
      const [response, responseBody] = (await apiRequest(
        `${API}/company/${companyId}/asset/search`,
        "POST",
        requestBody
      )) as [Response, PostResponseBody];
      setLoading(false);

      if (response.ok && responseBody) {
        setAssets(responseBody.assets);
        setDeviceStatuses(responseBody.deviceStatuses);
        setDevices(responseBody.devices);
        setErrorMessage("");
      } else {
        setErrorMessage(await getApiError(response, "Unable to search assets."));
      }
    }
  }

  // Reset states when toggling between basic and advanced search.
  function handleToggleAdvancedSearch(): void {
    setIsAdvancedSearch((prev) => !prev);
    setBasicSearchQuery("");
    setAssetName("");
    setAssetNickname("");
    setProductType("");
    setProductManufacturer("");
    setProductModel("");
    setProductIdentifier("");
    setControllerModel("");
    setDeviceModel("");
    setDeviceIdentifier("");
    setAssets([]);
    setDeviceStatuses([]);
    setDevices([]);
    setSelectedTabId("assets");
    setErrorMessage("");
  }

  // Check if the user pressed enter, if they did we search for devices.
  function checkPressEnter(e: React.KeyboardEvent): Promise<void> | void {
    if (e.key === "Enter") {
      e.preventDefault();
      return assetSearch();
    }
  }

  return (
    <div className={`${styles.body} p-4`}>
      <Spinner loading={loading} />
      <Card title={isAdvancedSearch ? "Advanced Search" : "Basic Search"}>
        <form className="m-4" onKeyDown={(e) => checkPressEnter(e)}>
          {isAdvancedSearch ? (
            <AdvancedSearch
              assetName={assetName}
              assetNickname={assetNickname}
              productType={productType}
              productManufacturer={productManufacturer}
              productModel={productModel}
              productIdentifier={productIdentifier}
              controllerModel={controllerModel}
              deviceModel={deviceModel}
              deviceIdentifier={deviceIdentifier}
              productTypes={productTypes}
              controllers={controllers}
              deviceTypes={deviceTypes}
              onAssetNameChange={(value) => setAssetName(value)}
              onAssetNicknameChange={(value) => setAssetNickname(value)}
              onProductTypeChange={(value) => setProductType(value)}
              onProductManufacturerChange={(value) => setProductManufacturer(value)}
              onProductModelChange={(value) => setProductModel(value)}
              onProductIdentifierChange={(value) => setProductIdentifier(value)}
              onControllerModelChange={(value) => setControllerModel(value)}
              onDeviceModelChange={(value) => setDeviceModel(value)}
              onDeviceIdentifierChange={(value) => setDeviceIdentifier(value)}
            />
          ) : (
            <div className={`${styles.searchInputRow} row justify-content-center`}>
              <div className="mb-1">
                <label className="mb-1">Search Query</label>
                <input
                  data-test="search-asset-name-input"
                  className={`${styles.searchInputField} form-control mx-auto`}
                  type="text"
                  value={basicSearchQuery}
                  onChange={(e) => setBasicSearchQuery(e.target.value)}
                />
              </div>
            </div>
          )}
          <div className={`${styles.searchInputRow} row`}>
            <div className="mb-1 col float-start">
              <button
                type="button"
                data-test="toggle-advanced-search-button"
                className={`${styles.searchButton} btn btn-secondary my-3`}
                disabled={loading}
                onClick={() => handleToggleAdvancedSearch()}
              >
                {isAdvancedSearch ? "Enable Basic Search" : "Enable Advanced Search"}
              </button>
            </div>
            <div className="mb-1 col">
              <button
                type="button"
                data-test="search-submit-button"
                className={`${styles.searchButton} btn btn-primary float-end my-3`}
                disabled={loading}
                onClick={() => assetSearch()}
              >
                Search
              </button>
            </div>
          </div>

          <Error message={errorMessage} />
        </form>
      </Card>
      <SearchListContainer
        loading={loading}
        tabs={tabs}
        assets={assets}
        devices={formatDevices(devices, deviceStatuses)}
        deviceStatuses={deviceStatuses}
        selectedTabId={selectedTabId}
        onClickTab={(tabId) => setSelectedTabId(tabId)}
      />
    </div>
  );
}

type Params = {
  initialSearchQuery: string;
};

interface PostResponseBody {
  assets: Asset[];
  deviceStatuses: DeviceStatus[];
  devices: Device[];
}

interface Asset {
  assetId: number;
  name: string;
  nickname: string;
  productType: string;
  productModel: string;
  productIdentifier: string;
  companyId: number;
  companyName: string;
  deviceType: string;
  deviceIdentifier: string;
}

interface DeviceStatusResponseBody {
  code: string;
  description: string;
  deviceStatusId: number;
  name: string;
  value: string;
}

interface Device {
  deviceId: number;
  deviceType: string;
  identifier: string;
  assetName: string;
  subscriptionType: string;
  activationStatus: string;
  subscriptionStartDate: string;
  subscriptionExpiry: string;
  subscriptionNumber: string;
  statuses: Status[];
}

interface DeviceStatus {
  code: string;
  description: string;
  deviceStatusId: number;
  name: string;
  value: string;
}

interface Status {
  deviceStatusId: number;
  value: string;
}

interface Tab {
  tabId: string;
  name: string;
  shortName: string;
  breakPoints: string;
}

interface GetResponseBody {
  productTypes: string[];
  productManufacturers: string[];
  controllers: ControllerSelection[];
  deviceTypes: DeviceTypeSelection[];
}

interface ControllerSelection {
  model: string;
  name: string;
}

interface DeviceTypeSelection {
  model: string;
  name: string;
}
